import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { IDetailPageState } from '../../reducers'

import {
  DetailsPageClient,
  PurchaseActionContext,
} from 'DVPAWebWidgetsDetailsPageClient'

let PurchaseFlowBootloader: typeof DetailsPageClient
if (!global.__SERVER__ || global.__DEVSERVER__) {
  PurchaseFlowBootloader = require('DVPAWebWidgetsDetailsPageClient')
    .DetailsPageClient
}

import {
  purchaseSelector,
  offerTokenToPurchaseActionSelector,
} from '../../selectors/purchase'
import { subPageTypeSelector } from '../../selectors/detailFragment'
import { DetailPageActions } from '../../actions'
import { Dispatch } from 'redux'
import {
  purchaseCancelled,
  purchaseFailed,
  refreshAfterDone,
  triggerPurchase,
  refreshCSRF,
  CallbackInput,
} from '../../actions/purchase'
import { withLogger, ILogger } from '../../context/withLogger'

import { COUNTER_PACKAGE_PREFIX } from '../../utils/utils'
import { ErrorBoundary } from '../error/ErrorBoundary'
import { onPlayerCapabilities, PlayerCapabilities } from '../../integrations/playerIntegrationAui'

type OwnProps = ILogger
export type IPurchaseFlowBootloader = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps

type State = {
  initialized: boolean
  supportsWebPlayback: boolean
  supportsHDMovies: boolean
}

const RESTART_PURCHASE_DELAY = 300 // To allow time for player capabilities to be retrieved
const PURCHASE_ELEMENT_ID = 'mfa-purchase-root'

const getQueryParam = (param: string) => {
  const params = window.location.search
  const result = (params || '').match(`${param}=([^&]+)`)
  return result && decodeURIComponent(result[1])
}

export class ConnectedPurchaseFlowWrapper extends React.Component<
  IPurchaseFlowBootloader
> {
  private el: HTMLDivElement | undefined
  private portalRoot: HTMLElement | null
  state: State

  constructor(props: IPurchaseFlowBootloader) {
    super(props)
    this.state = {
      initialized: false,
      supportsWebPlayback: false,
      supportsHDMovies: false,
    }
    this.portalRoot = null
  }

  componentDidMount() {
    const { triggerPurchase, usePlayer } = this.props

    const setCapabilities = (capabilities: PlayerCapabilities) => {
      this.setState({
        supportsWebPlayback: Boolean(capabilities.supportsWebPlayback),
        supportsHDMovies: Boolean(capabilities.supportsHDMovies),
      })
    }

    if (!usePlayer) {
      onPlayerCapabilities(setCapabilities, this.props.logger)
    }

    if (this.shouldInitialize()) {
      this.initialize()

      const continueToken = getQueryParam('continueToken')
      const offerToken = getQueryParam('offerToken')
      if (continueToken && offerToken) {
        setTimeout(
          () => triggerPurchase(offerToken, continueToken),
          RESTART_PURCHASE_DELAY
        )
      }
    }
  }

  componentDidUpdate() {
    if (!this.state.initialized && this.shouldInitialize()) {
      this.initialize()
    }
  }

  componentWillUnmount() {
    if (this.portalRoot && this.el) {
      this.portalRoot.removeChild(this.el)
    }
  }

  getPurchaseElement() {
    let el = document.getElementById(PURCHASE_ELEMENT_ID)

    if (el) {
      return el
    }

    el = document.createElement('div')
    el.setAttribute('id', PURCHASE_ELEMENT_ID)
    document.body.appendChild(el)
    return el
  }

  initialize() {
    this.portalRoot = this.getPurchaseElement()
    this.el = document.createElement('div')
    this.portalRoot.appendChild(this.el)
    this.setState({ initialized: true })
  }

  shouldInitialize = () =>
    Object.keys(this.props.offerTokenToPurchaseAction).length > 0 &&
    this.props.useMFA

  render() {
    const {
      purchasingDetails,
      callbackCancel,
      resetAfterCancel,
      callbackFailure,
      failed,
      refreshAfterDone,
      callbackCSRF,
      subPageType,
      titleId,
      capabilities,
    } = this.props

    const { initialized, supportsWebPlayback, supportsHDMovies } = this.state

    const playerDetails = capabilities
      ? {
          supportsWebPlayback: capabilities.supportsWebPlayback,
          supportsHDMovies: capabilities.supportsHDMovies,
        }
      : {
          supportsWebPlayback,
          supportsHDMovies,
        }

    if (!initialized) {
      return null
    }

    const actionContext: PurchaseActionContext = {
      isVisible: Boolean(purchasingDetails),
      playerDetails,
      extraData: {
        subPageType,
        titleId,
      },
      callbacks: {
        callbackCancel,
        callbackFailure,
        resetAfterCancel,
        refreshAfterDone,
        failed,
        callbackCSRF,
      },
    }

    if (purchasingDetails) {
      const { details, continueToken, refTag } = purchasingDetails
      const offerToken = details && details.offer.offerToken
      const csrfToken = details && details.offer.csrfTokenWorkflow
      const gti = details && details.gti

      actionContext.extraData.refTag = refTag
      actionContext.extraData.globalTitleIdentifier = gti
      actionContext.offerContext = { offerToken, csrfToken }
      actionContext.continueToken = continueToken
    }

    return ReactDOM.createPortal(
      <ErrorBoundary name="purchaseWorkflow">
        <PurchaseFlowBootloader {...actionContext} />
      </ErrorBoundary>,
      this.el!
    )
  }
}

const mapStateToProps = (state: IDetailPageState) => ({
  purchasingDetails: purchaseSelector(state),
  offerTokenToPurchaseAction: offerTokenToPurchaseActionSelector(state),
  subPageType: subPageTypeSelector(state),
  titleId: state.pageTitleId,
  useMFA: state.features.useMFA,
  usePlayer: state.features.usePlayer,
  capabilities: state.player.capabilities,
})

type callbackType = (props: CallbackInput) => unknown

const mapDispatchToProps = (
  dispatch: Dispatch<DetailPageActions>,
  { logger }: OwnProps
) => {
  const logCallback = (
    callback: callbackType,
    callbackName: string,
    isError: boolean = false
  ) => {
    const log = isError ? logger.error : logger.info
    log(`[MFA] - Callback ${callbackName} triggered`).count(
      `${COUNTER_PACKAGE_PREFIX}_MFA_${callbackName}`,
      1,
      true
    )
    callback({ dispatch, logger })
  }

  return {
    callbackCancel: () => logCallback(purchaseCancelled, 'callbackCancel'),
    resetAfterCancel: () =>
      logCallback(purchaseCancelled, 'resetAfterCancel'),
    callbackFailure: () =>
      logCallback(purchaseFailed, 'callbackFailure', true),
    failed: () => logCallback(purchaseFailed, 'failed', true),
    refreshAfterDone: () =>
      logCallback(refreshAfterDone, 'refreshAfterDone'),
    callbackCSRF: () => logCallback(refreshCSRF, 'callbackCSRF'),
    triggerPurchase: (offerToken: string, continueToken: string) =>
      triggerPurchase(dispatch, logger, offerToken, continueToken),
  }
}

export const PurchaseFlowWrapper = withLogger(
  connect(mapStateToProps, mapDispatchToProps)(ConnectedPurchaseFlowWrapper)
)
